import { useState, useEffect, useMemo } from "react";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./AssegnazioniGg.css";
import Lf23Loader from "../../../components/Lf23Loader";

const AssegnazioniGg = ({ filters, loading: parentLoading }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [tableData, setTableData] = useState([]);
    const [filterLoading, setFilterLoading] = useState(true);
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'data_assunzione', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const sortedTableData = useMemo(() => {
        if (!sortConfig.key) return tableData;
        const toComparable = (val) => {
            if (val === null || val === undefined) return '';
            const s = String(val);
            // Converte DD/MM/YYYY → YYYY-MM-DD per ordinamento corretto
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
                const [d, m, y] = s.split('/');
                return `${y}-${m}-${d}`;
            }
            const num = parseFloat(s);
            return !isNaN(num) ? num : s;
        };
        return [...tableData].sort((a, b) => {
            const ca = toComparable(a[sortConfig.key]);
            const cb = toComparable(b[sortConfig.key]);
            const cmp = typeof ca === 'number' && typeof cb === 'number'
                ? ca - cb
                : String(ca).localeCompare(String(cb));
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [tableData, sortConfig]);

    // Escape per uscire da fullscreen
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsFullscreenTable(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Quando i filtri cambiano, mostra il loader
    useEffect(() => {
        setFilterLoading(true);
    }, [filters]);

    // Carica dati tabella quando cambiano i filtri dal padre
    useEffect(() => {
        const fetchData = async () => {
            try {
                const params = {
                    dataRiferimento: filters.toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipoContratto: filters.tipoContratto?.map(t => t.value).join(",") || "",
                    dettaglioContratto: filters.dettaglioContratto?.map(d => d.value).join(",") || "",
                    motivoCessazione: filters.motivoCessazione?.map(m => m.value).join(",") || "",
                    sesso: filters.sesso?.map(s => s.value).join(",") || "",
                    eta: filters.eta?.map(e => e.value).join(",") || ""
                };
                const res = await axios.get(`${API_URL}/hr-dettaglio/assegnazioni-gg`, { params });
                setTableData(res.data.tableData || []);
            } catch (error) {
                console.error("Errore nel caricamento dati Assegnazioni GG:", error);
            } finally {
                setFilterLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters]);

    const prepareExcelData = () => {
        const headers = [
            "Operatore",
            "Attivo",
            "Sesso",
            "Data Nascita",
            "Età",
            "Data Assunzione",
            "Tipologia",
            "Rapp Lav",
            "Monte Ore Attivo",
            "Livello",
            "Svan SI",
            "Svan NO",
            "Desc Svan",
            "Settore",
            "Commessa",
            "Livello 1",
            "Livello 2"
        ];

        const rows = sortedTableData.map(row => [
            row.operatore || "-",
            row.attivo ?? "-",
            row.sesso || "-",
            row.data_nascita || "-",
            row.eta ?? "-",
            row.data_assunzione || "-",
            row.tipologia || "-",
            row.rapporto_lavoro || "-",
            row.monte_ore_attivo ?? "-",
            row.livello || "-",
            row.svan_si ?? "-",
            row.svan_no ?? "-",
            row.desc_svan || "-",
            row.settore || "-",
            row.commessa || "-",
            row.livello1 || "-",
            row.livello2 || "-"
        ]);

        return { headers, rows };
    };

    const handleExportExcel = async () => {
        try {
            const excelData = prepareExcelData();

            const response = await axios.post(
                `${API_URL}/hr-dettaglio/downloadExcel`,
                {
                    tableName: "Assegnazioni GG",
                    datiDaStampare: excelData
                },
                { responseType: "blob" }
            );

            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", "assegnazioni_gg.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Errore durante l'export Excel:", error);
        }
    };

    const isLoading = parentLoading || filterLoading;

    return (
        <div className="assegnazioni-gg-container">
            {/* Tabella */}
            {isLoading ? (
                <Lf23Loader />
            ) : tableData.length === 0 ? (
                <div className="oper-hr-empty-state">Nessun dato disponibile</div>
            ) : (
                <div className={`assegnazioni-gg-table-section${isFullscreenTable ? ' fullscreen-assegnazioni-gg' : ''}`}>
                    <RotateDevice active={isFullscreenTable} />
                    <div className="assegnazioni-gg-table-header">
                        <button
                            className="assegnazioni-gg-download-btn"
                            onClick={handleExportExcel}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Esporta
                        </button>
                        <button
                            className="assegnazioni-gg-fullscreen-btn"
                            onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                            title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreenTable ? (
                                    <>
                                        <polyline points="4 14 10 14 10 20"></polyline>
                                        <polyline points="20 10 14 10 14 4"></polyline>
                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </>
                                ) : (
                                    <>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <polyline points="9 21 3 21 3 15"></polyline>
                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </>
                                )}
                            </svg>
                        </button>
                    </div>
                    <div className="assegnazioni-gg-table-wrapper">
                        <table className="assegnazioni-gg-table">
                            <thead>
                                <tr>
                                    {[
                                        { label: 'Operatore', key: 'operatore' },
                                        { label: 'Attivo', key: 'attivo' },
                                        { label: 'Sesso', key: 'sesso' },
                                        { label: 'Data Nascita', key: 'data_nascita' },
                                        { label: 'Età', key: 'eta' },
                                        { label: 'Data Assunzione', key: 'data_assunzione' },
                                        { label: 'Tipologia', key: 'tipologia' },
                                        { label: 'Rapp Lav', key: 'rapporto_lavoro' },
                                        { label: 'Monte Ore Attivo', key: 'monte_ore_attivo' },
                                        { label: 'Livello', key: 'livello' },
                                        { label: 'Svan SI', key: 'svan_si' },
                                        { label: 'Svan NO', key: 'svan_no' },
                                        { label: 'Desc Svan', key: 'desc_svan' },
                                        { label: 'Settore', key: 'settore' },
                                        { label: 'Commessa', key: 'commessa' },
                                        { label: 'Livello 1', key: 'livello1' },
                                        { label: 'Livello 2', key: 'livello2' },
                                    ].map(({ label, key }) => (
                                        <th key={key} className="assegnazioni-gg-th-sortable" onClick={() => handleSort(key)}>
                                            {label}
                                            <span className="assegnazioni-gg-sort-icon">
                                                {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTableData.map((row, index) => (
                                    <tr key={index}>
                                        <td>{row.operatore}</td>
                                        <td>{row.attivo}</td>
                                        <td>{row.sesso}</td>
                                        <td>{row.data_nascita}</td>
                                        <td>{row.eta}</td>
                                        <td>{row.data_assunzione}</td>
                                        <td>{row.tipologia}</td>
                                        <td>{row.rapporto_lavoro}</td>
                                        <td>{row.monte_ore_attivo}</td>
                                        <td>{row.livello}</td>
                                        <td>{row.svan_si}</td>
                                        <td>{row.svan_no}</td>
                                        <td>{row.desc_svan}</td>
                                        <td>{row.settore}</td>
                                        <td>{row.commessa}</td>
                                        <td>{row.livello1}</td>
                                        <td>{row.livello2}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2} className="assegnazioni-gg-totale">
                                        Totale: {tableData.length}
                                    </td>
                                    <td colSpan={15}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssegnazioniGg;